import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import Individual from "marketplace/individuals/individual";
import { type ReactNode } from "react";
import useIndividualCardTrigger from "marketplace/individuals/profiles/_views/use-individual-card-trigger";
import {
  proposalFeeScheduleRestrictedTeamMemberText,
  proposalFeeScheduleTeamMemberFallbackText,
} from "work/proposals/negotiation/proposal-fee-schedule-aspects";
import {
  FeeScheduleTableCellNoValue,
  FeeScheduleTableCellText,
} from "./fee-schedule-table-cell";
import FeeScheduleDepartedTeamMember, { type FeeScheduleTeamMemberDeparture } from "./fee-schedule-departed-rate";

/**
 * The read-only table turns pointer events off on every body row so a row reads as a record rather
 * than a control. The name is the one thing on the row that is a control, so it takes them back.
 */
const NameButton = styled(Link)(() => ({
  maxWidth: "100%",
  overflow: "hidden",
  pointerEvents: "auto",
  textAlign: "left",
  textOverflow: "ellipsis",
  verticalAlign: "baseline",
  whiteSpace: "nowrap",
  // Cast back to `Link` so `component="button"` keeps its typing; `styled` drops MUI's overloads.
})) as typeof Link;

type FeeScheduleTeamMemberCellProps = {
  teamMember: { userId: Guid } | null | undefined;
  teamMemberIsMasked: boolean;
  individual: Individual | undefined;
  departure: FeeScheduleTeamMemberDeparture | undefined;
  /** Draws the words naming who holds the rate, so a table can mark its search in them (#6590). */
  renderName?: (name: string) => ReactNode;
};

/**
 * Who holds one fee schedule category's rate, in a read-only fee schedule (#5811).
 *
 * The words are the editable proposal table's own, so a rate reads the same on every surface —
 * except that nobody assigned is the table's dash rather than "Unassigned", as #5811 asked.
 *
 * - Masked: "Restricted", because the reader may not see the vendor team.
 * - Nobody assigned: the table's dash for "no value".
 * - Somebody the directory will not name: "Team member".
 * - Somebody named: their name. Pointing at it peeks at their profile card, and a click pins the card
 *   open until the next click (#5813; see `useIndividualCardTrigger`).
 * - Somebody who has since left the team: the name, struck through and grayed, followed by how they
 *   left. The card still opens: a reader wants to know who the rate was agreed for. On live work the
 *   rate keeps their name until a change order restaffs it (#6096).
 */
export default function FeeScheduleTeamMemberCell(props: Readonly<FeeScheduleTeamMemberCellProps>) {
  const { teamMember, teamMemberIsMasked, individual, departure } = props;
  const renderName = props.renderName ?? ((text: string) => text);
  // Above the early returns, so every render calls the same hooks.
  const { triggerProps, card } = useIndividualCardTrigger(individual);

  if (teamMemberIsMasked) {
    return (
      <FeeScheduleTableCellText color="text.secondary" title={proposalFeeScheduleRestrictedTeamMemberText}>
        {renderName(proposalFeeScheduleRestrictedTeamMemberText)}
      </FeeScheduleTableCellText>
    );
  }
  if (!teamMember) return <FeeScheduleTableCellNoValue />;

  const name = individual?.fullName || proposalFeeScheduleTeamMemberFallbackText;
  const nameContent = individual ? (
    <NameButton
      component="button"
      type="button"
      variant="body2"
      underline="hover"
      color="inherit"
      {...triggerProps}
    >
      {renderName(name)}
    </NameButton>
  ) : (
    renderName(name)
  );

  return (
    <>
      {departure ? (
        <FeeScheduleDepartedTeamMember name={name} departure={departure}>{nameContent}</FeeScheduleDepartedTeamMember>
      ) : (
        <FeeScheduleTableCellText title={name}>{nameContent}</FeeScheduleTableCellText>
      )}
      {card}
    </>
  );
}
